import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  Input,
  InputNumber,
  Loading,
  MessagePlugin,
  Select,
  Textarea,
} from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { MinusCircleIcon } from 'tdesign-icons-react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { find, isEmpty } from 'lodash';
import { addVariable, getVariable, updateVariable } from '@/services/variableService';
import {
  sameValueOptions,
  showTypeOptions,
  variableLevelOptions,
  variableOptionalOptions,
} from '@/utils/constant';
import Text from '@/components/Text';
import { isValidJSON, isVoid, toTsOptions } from '@/utils/common';
import TextSelect from '@/components/TextSelect';
import { doBuried } from '@/services/buriedService';
import Form from '@/components/Form';
import VarInput, { convertDetailToProps } from '../../components/VarInput';

const { FormItem, List } = Form;

function Modal({
  visible,
  handleClose,
  id,
  group,
  dependOptions: _dependOptions,
  stencilList,
  scopeType,
  eventId,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const isEdit = !isVoid(id);

  const showType = Form.useWatch('showType', form); // 展现形式
  const dependId = Form.useWatch('dependId', form); // 依赖
  const showDetail = Form.useWatch('showDetail', form); // 展示参数
  const sameValue = Form.useWatch('sameValue', form); // 相同值
  const variableType = Form.useWatch('variableType', form); // 是否必填 0 必填，1选填，选填变量必须有默认值

  // 剔除自身
  const dependOptions = _dependOptions.filter((item) => item.value !== id);

  const currentDepend = dependOptions.find((item) => item.value === dependId);

  // 动态输入参数
  const inputProps = useMemo(() => {
    // 如果下拉框，添加value
    if (showType === 2) {
      const options = showDetail?.options?.map((item) => ({
        label: item.value, // label 也用 value
        value: item.value,
      }));
      return { options };
    }
    return showDetail;
  }, [showDetail, showType]);

  const handleSubmit = async (fields) => {
    const values = {
      ...fields,
      scopeType,
      // values中的value统一转字符串
      values: isEmpty(fields.values)
        ? []
        : fields.values.map((item) => ({
            ...item,
            value: isVoid(item.value) ? null : String(item.value),
          })),
    };
    // 转换字符串
    values.showDetail = JSON.stringify(inputProps);

    try {
      setSubmitting(true);
      if (isEdit) {
        await updateVariable({
          ...values,
          id,
        });
      } else {
        await addVariable({ ...values, groupId: group.id });
      }
      MessagePlugin.success('操作成功');
      handleClose(true);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }

    // 新增埋点
    if (!isEdit) {
      doBuried({ eventId });
    }
  };

  const doInit = useMemoizedFn(async () => {
    if (visible) {
      form.setFields([{ name: 'groupName', value: group.groupName }]); // 显示分组名字
      if (scopeType === 1) {
        // k8s 无IP变量
        form.setFields([{ name: 'variableLevel', value: 0 }]);
      }
      if (isEdit) {
        setLoading(true);
        try {
          const { data: detail } = await getVariable(id);
          const originValues = detail.values || [];

          // 检查是否有不同的值
          let sameValue = true;
          originValues.forEach((item) => {
            if (item.value !== detail.defaultValue) {
              sameValue = false;
            }
          });

          // 重新根据stencilList同顺序排序，保证赋值的正确性
          const values = [];
          stencilList.forEach((stencil) => {
            values.push(find(originValues, { stencilId: stencil.id }));
          });
          const data = {
            ...detail,
            sameValue,
            values,
            showDetail: isValidJSON(detail.showDetail) ? JSON.parse(detail.showDetail) : null,
          };

          form.setFieldsValue(data);
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          setLoading(false);
        }
      }
    } else {
      form.resetFields();
    }
  });

  useEffect(() => {
    doInit();
  }, [visible, doInit]);

  const typeChange = (value) => {
    form.resetFields(['showDetail', 'defaultValue', 'sameValue']);
    if (value === 2) {
      // 下拉
      form.setFieldValue(['showDetail', 'options'], [{ value: '' }]);
    }
  };

  return (
    <Dialog
      header={isEdit ? '修改变量' : '新增变量'}
      visible={visible}
      onClose={() => handleClose()}
      confirmBtn={{ loading: submitting, content: '确定' }}
      onConfirm={() => form.submit()}
      width={550}
      placement='center'
    >
      <Loading loading={loading} size='small'>
        <Form style={{ width: '90%' }} form={form} onFinish={handleSubmit}>
          <FormItem name='groupName' label='变量组' rules={[{ required: true }]}>
            <Text />
          </FormItem>
          <FormItem name='key' label='变量key' rules={[{ required: true }]}>
            {isEdit ? <Text /> : <Input />}
          </FormItem>
          <FormItem name='keyName' label='展示名称' rules={[{ required: true }]}>
            <Input />
          </FormItem>
          <FormItem name='variableLevel' label='变量范围' rules={[{ required: true }]}>
            {scopeType === 1 ? (
              <TextSelect options={variableLevelOptions} />
            ) : (
              <Select options={toTsOptions(variableLevelOptions)} />
            )}
          </FormItem>
          <FormItem name='variableType' label='变量类型' rules={[{ required: true }]}>
            <Select options={toTsOptions(variableOptionalOptions)} />
          </FormItem>
          <FormItem name='showType' label='展现形式' rules={[{ required: true }]}>
            <Select options={toTsOptions(showTypeOptions)} onChange={typeChange} />
          </FormItem>
          {/* 文本框 */}
          {showType === 1 && (
            <Block>
              <FormItem name={['showDetail', 'prepend']} label='前缀'>
                <Input />
              </FormItem>
              <FormItem name={['showDetail', 'append']} label='后缀'>
                <Input />
              </FormItem>
            </Block>
          )}
          {showType === 2 && (
            <Block>
              <List name={['showDetail', 'options']}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <FormItem key={key}>
                        <FormItem {...restField} name={[name, 'value']} label='选项值'>
                          <Input />
                        </FormItem>

                        {index > 0 && (
                          <FormItem>
                            <MinusCircleIcon
                              style={{ cursor: 'pointer' }}
                              onClick={() => remove(name)}
                            />
                          </FormItem>
                        )}
                      </FormItem>
                    ))}
                    <FormItem style={{ marginLeft: 100 }}>
                      <Button theme='default' variant='dashed' onClick={() => add({ value: '' })}>
                        新增一条
                      </Button>
                    </FormItem>
                  </>
                )}
              </List>
            </Block>
          )}
          {showType === 3 && (
            <Block>
              <FormItem name={['showDetail', 'min']} label='最小值'>
                <InputNumber theme='normal' />
              </FormItem>
              <FormItem name={['showDetail', 'max']} label='最大值'>
                <InputNumber theme='normal' />
              </FormItem>
              <FormItem name={['showDetail', 'decimalPlaces']} label='小数位数'>
                <InputNumber decimalPlaces={0} min={0} allowInputOverLimit={false} theme='normal' />
              </FormItem>
              <FormItem name={['showDetail', 'suffix']} label='单位'>
                <Input style={{ width: 144 }} />
              </FormItem>
            </Block>
          )}
          <FormItem name='dependId' label='变量依赖'>
            <Select options={toTsOptions(dependOptions)} clearable />
          </FormItem>
          {dependId && (
            <FormItem name='dependValue' label='值等于'>
              <VarInput
                type={currentDepend.showType}
                inputProps={convertDetailToProps(currentDepend.showDetail)}
              />
            </FormItem>
          )}
          <FormItem name='desc' label='变量备注' rules={[{ required: true }]}>
            <Textarea placeholder='变量备注' />
          </FormItem>

          <FormItem
            name='sameValue'
            labelAlign='top'
            style={{ paddingLeft: 10 }}
            label='其他默认值模板中的值和default保持一致'
            rules={[{ required: true }]}
          >
            <Select options={toTsOptions(sameValueOptions)} />
          </FormItem>
          {sameValue === true && (
            <FormItem
              name='defaultValue'
              label='default默认值模板中的值'
              labelAlign='top'
              rules={[{ required: variableType === 1 }]}
              style={{ paddingLeft: 10 }}
            >
              <VarInput type={showType} inputProps={inputProps} />
            </FormItem>
          )}
          {sameValue === false &&
            stencilList.map((item, index) => (
              <React.Fragment key={item.id}>
                <FormItem
                  name={['values', index, 'stencilId']}
                  initialValue={item.id}
                  style={{ display: 'none' }}
                />
                <FormItem
                  name={['values', index, 'value']}
                  label={item.stencilName}
                  labelAlign='top'
                  rules={[{ required: variableType === 1 }]}
                  style={{ paddingLeft: 10 }}
                >
                  <VarInput type={showType} inputProps={inputProps} />
                </FormItem>
              </React.Fragment>
            ))}
        </Form>
      </Loading>
    </Dialog>
  );
}

export default connect((state) => ({
  stencilList: state.variable.stencilList,
}))(Modal);

const Block = styled.div`
  border-radius: 12px;
  background: #f7f8fa;
  padding: 20px;
  margin-bottom: 20px;
`;
